
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Diamond, Sparkles, Clock, MessageSquare } from 'lucide-react';

interface PreLaunchSponsoredExperienceProps {
  launchDate: Date;
}

export const PreLaunchSponsoredExperience: React.FC<PreLaunchSponsoredExperienceProps> = ({
  launchDate
}) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const launch = launchDate.getTime();
      const difference = launch - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(difference % (1000 * 60) / 1000)
        });
      } else {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [launchDate]);

  const handleFeedbackClick = () => {
    navigate('/main/chat/Administrador');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="max-w-3xl mx-auto space-y-6 mb-16">
        {/* Header com Crown */}
        <div className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-luxury-gold to-yellow-600 rounded-full flex items-center justify-center animate-pulse">
            <Crown className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-luxury-gold via-yellow-400 to-luxury-gold bg-clip-text text-transparent">
            Experiência Exclusiva
          </h1>
          <p className="text-sm md:text-lg text-white/80">
            em Desenvolvimento
          </p>
        </div>

        {/* Contador Regressivo */}
        <Card className="bg-black/40 border-luxury-gold/30 backdrop-blur-sm">
          <CardContent className="p-4 md:p-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 text-luxury-gold">
                <Clock className="h-5 w-5" />
                <span className="text-sm md:text-base font-medium">Lançamento em:</span>
              </div>
              
              <div className="grid grid-cols-4 gap-2 md:gap-4 max-w-sm mx-auto">
                <div className="text-center">
                  <div className="bg-luxury-gold/20 border border-luxury-gold/40 rounded-lg p-2 md:p-4">
                    <div className="text-xl md:text-3xl font-bold text-white">
                      {timeLeft.days.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs md:text-sm text-luxury-gold uppercase tracking-normal">Dias</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-luxury-gold/20 border border-luxury-gold/40 rounded-lg p-2 md:p-4">
                    <div className="text-xl md:text-3xl font-bold text-white">
                      {timeLeft.hours.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs md:text-sm text-luxury-gold uppercase tracking-normal">Horas</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-luxury-gold/20 border border-luxury-gold/40 rounded-lg p-2 md:p-4">
                    <div className="text-xl md:text-3xl font-bold text-white">
                      {timeLeft.minutes.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs md:text-sm text-luxury-gold uppercase tracking-normal">Min</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-luxury-gold/20 border border-luxury-gold/40 rounded-lg p-2 md:p-4">
                    <div className="text-xl md:text-3xl font-bold text-white">
                      {timeLeft.seconds.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs md:text-sm text-luxury-gold uppercase tracking-normal">Seg</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mensagem Principal - Minimalista */}
        <Card className="bg-black/40 border-gray-800 backdrop-blur-sm">
          <CardContent className="p-4 md:p-6 space-y-3 md:space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <Diamond className="h-5 w-5 text-luxury-gold" />
              <h2 className="text-lg md:text-2xl font-bold text-white">Em Breve</h2>
            </div>
            
            <p className="text-sm md:text-base text-white/90 leading-relaxed">
              Finalizando os últimos detalhes.
            </p>
            
            <div className="bg-luxury-gold/10 border border-luxury-gold/20 rounded-lg p-3 md:p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-luxury-gold mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-luxury-gold font-semibold text-sm md:text-lg mb-1 md:mb-2">
                    O que esperar:
                  </h3>
                  <ul className="text-white/80 space-y-1 text-xs md:text-sm">
                    <li>• Perfis exclusivos verificados</li>
                    <li>• Compatibilidade inteligente</li>
                    <li>• Conexões premium</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Feedback Section */}
        <Card className="bg-black/40 border-gray-800 backdrop-blur-sm">
          <CardContent className="p-4 md:p-6 text-center space-y-3 md:space-y-4">
            <div className="flex items-center justify-center gap-2">
              <MessageSquare className="h-5 w-5 text-luxury-gold" />
              <h3 className="text-base md:text-xl font-bold text-white">Sua Opinião é Valiosa</h3>
            </div>
            
            <p className="text-xs md:text-sm text-white/80">
              Compartilhe suas expectativas diretamente com nossa equipe.
            </p>
            
            <Button 
              onClick={handleFeedbackClick} 
              className="bg-gradient-to-r from-luxury-gold to-yellow-600 hover:from-yellow-600 hover:to-luxury-gold text-black font-semibold px-6 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 text-sm"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Falar com o Desenvolvedor
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Diamond className="h-4 w-4 text-luxury-gold" />
            <span className="text-luxury-gold font-medium text-sm">INTERLACED</span>
            <Diamond className="h-4 w-4 text-luxury-gold" />
          </div>
          <p className="text-white/60 max-w-md mx-auto text-xs md:text-sm">
            Sua jornada extraordinária está prestes a começar.
          </p>
          <div className="flex items-center justify-center gap-1">
            <Sparkles className="h-3 w-3 text-luxury-gold animate-pulse" />
            <span className="text-xs text-luxury-gold">Excelência em cada detalhe</span>
            <Sparkles className="h-3 w-3 text-luxury-gold animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
};
